// Artifacts hooks. The Artifacts tab on the case page lists existing
// artifacts and offers buttons to generate new ones.
//
// `useFirmMessage()` POSTs to /cases/:id/artifacts/firm-message which
// returns the freshly-rendered PDF as the response body. The hook reads it
// as a Blob and triggers a browser download — the Artifacts list refetches
// after, so the user sees the row in `ready` state.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiFetch } from './api';
import { useMe } from './auth';

export type CaseArtifactKind =
  | 'firm_message'
  | 'case_brief'
  | 'registered_notice'
  | 'routing_letter'
  | 'evidence_zip'
  | 'lawyer_handoff';

export type ContractorResponseKind =
  | 'no_reply'
  | 'partial_reply'
  | 'agreed_to_fix'
  | 'refused'
  | 'other';

export type CaseArtifact = {
  id: string;
  case_id: string;
  kind: CaseArtifactKind;
  status: 'generating' | 'ready' | 'failed';
  storage_key: string | null;
  size_bytes: string | null;
  content_type: string;
  template_version: string;
  legal_review_date: string | null;
  error: string | null;
  generated_at: string | null;
  /** Post-send outcome (migration 0023). Populated when the user logs
   * via the "How did it go?" banner. */
  sent_at: string | null;
  contractor_response_kind: ContractorResponseKind | null;
  contractor_response_note: string | null;
  response_logged_at: string | null;
  created_at: string;
};

export const artifactsQueryKey = (caseId: string) => ['cases', caseId, 'artifacts'] as const;

export function useArtifacts(caseId: string | undefined) {
  const me = useMe();
  return useQuery({
    queryKey: artifactsQueryKey(caseId ?? ''),
    queryFn: () => apiFetch<{ artifacts: CaseArtifact[] }>(`/cases/${caseId}/artifacts`),
    select: (d) => d.artifacts,
    enabled: !!caseId && !!me.data?.user,
  });
}

/**
 * Generate the free firm-message artifact and trigger a browser download
 * with the resulting PDF. Refetches the artifacts list on success so the
 * tab updates with the new "ready" row.
 */
export function useGenerateFirmMessage(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      // We need the raw response, not parsed JSON — apiFetch only does JSON.
      // Direct fetch via /api proxy. Same-origin, cookies attached.
      const res = await fetch(`/api/cases/${caseId}/artifacts/firm-message`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        let parsed: unknown;
        try {
          parsed = await res.json();
        } catch {
          parsed = await res.text().catch(() => undefined);
        }
        throw new ApiError(`firm-message generation failed: ${res.status}`, res.status, parsed);
      }
      const blob = await res.blob();
      const artifactId = res.headers.get('X-Artifact-Id') ?? null;
      return { blob, artifactId };
    },
    onSuccess: ({ blob }) => {
      // Trigger a browser download. URL.createObjectURL → anchor click → revoke.
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'interpelacao-amigavel.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      qc.invalidateQueries({ queryKey: artifactsQueryKey(caseId) });
    },
  });
}

/**
 * Download a previously-generated artifact by its id. Server returns a
 * 302 to a short-lived signed R2 URL, so we just point window.location.
 */
export function downloadArtifactUrl(caseId: string, artifactId: string): string {
  return `/api/cases/${caseId}/artifacts/${artifactId}`;
}

/** Shared helper: POST a JSON body, expect a binary download in response. */
async function postAndDownload(
  path: string,
  body: unknown,
  filename: string,
): Promise<{ blob: Blob; artifactId: string | null }> {
  const res = await fetch(`/api${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let parsed: unknown;
    try {
      parsed = await res.json();
    } catch {
      parsed = await res.text().catch(() => undefined);
    }
    throw new ApiError(`generation failed: ${res.status}`, res.status, parsed);
  }
  const blob = await res.blob();
  const artifactId = res.headers.get('X-Artifact-Id');
  // Trigger browser download.
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return { blob, artifactId };
}

export type RemedyKind = 'repair' | 'complete' | 'refund' | 'terminate';

export function useGenerateRegisteredNotice(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { remedy: RemedyKind; daysOverride?: number }) =>
      postAndDownload(
        `/cases/${caseId}/artifacts/registered-notice`,
        input,
        'interpelacao-admonitoria.pdf',
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: artifactsQueryKey(caseId) }),
  });
}

export function useGenerateRoutingLetter(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      postAndDownload(`/cases/${caseId}/artifacts/routing`, {}, 'recomendacao-encaminhamento.pdf'),
    onSuccess: () => qc.invalidateQueries({ queryKey: artifactsQueryKey(caseId) }),
  });
}

export function useGenerateLawyerHandoff(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      postAndDownload(`/cases/${caseId}/artifacts/lawyer-handoff`, {}, 'sumario-para-advogado.pdf'),
    onSuccess: () => qc.invalidateQueries({ queryKey: artifactsQueryKey(caseId) }),
  });
}

export type LogOutcomeInput = {
  artifactId: string;
  /** ISO datetime (or null to clear). Null is a legitimate state — "I
   * haven't sent it yet" should still let the user record `kind`. */
  sent_at: string | null;
  contractor_response_kind: ContractorResponseKind | null;
  contractor_response_note: string | null;
};

export function useLogArtifactOutcome(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LogOutcomeInput) =>
      apiFetch<{ ok: true }>(`/cases/${caseId}/artifacts/${input.artifactId}/outcome`, {
        method: 'PATCH',
        body: {
          sent_at: input.sent_at,
          contractor_response_kind: input.contractor_response_kind,
          contractor_response_note: input.contractor_response_note,
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: artifactsQueryKey(caseId) }),
  });
}

export function useGenerateEvidenceZip(caseId: string) {
  // No artifact row to invalidate (zip is streamed direct, no persistence).
  return useMutation({
    mutationFn: () =>
      postAndDownload(
        `/cases/${caseId}/artifacts/evidence-zip`,
        {},
        `evidence-${caseId.slice(0, 8)}.zip`,
      ),
  });
}
